import { Component, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { Router } from "@angular/router";
import { ApiService } from "../api.service";
import { IntegrationsService } from "../integrations/integrations.service";
import { SessionService } from "../session.service";
import { Subscription } from "rxjs";

type DocsIndexResponse = {
  title?: string;
  version?: string;
  description?: string;
  docs?: {
    openapiJson?: string;
    openapiYaml?: string;
    raml?: string;
    guide?: string;
  };
};

type OpenApiSpec = {
  info?: {
    title?: string;
    version?: string;
    description?: string;
  };
  paths?: Record<string, Record<string, OpenApiOperation>>;
  tags?: Array<{ name: string; description?: string }>;
  components?: {
    securitySchemes?: Record<
      string,
      { type?: string; in?: string; name?: string; description?: string }
    >;
  };
};

type OpenApiOperation = {
  summary?: string;
  description?: string;
  tags?: string[];
  parameters?: Array<{
    name?: string;
    in?: string;
    required?: boolean;
    description?: string;
    schema?: { type?: string };
  }>;
  requestBody?: {
    required?: boolean;
    content?: Record<
      string,
      {
        example?: unknown;
        examples?: Record<string, { summary?: string; value?: unknown }>;
      }
    >;
  };
  responses?: Record<
    string,
    {
      description?: string;
      headers?: Record<
        string,
        {
          description?: string;
          example?: unknown;
          schema?: { type?: string; example?: unknown };
        }
      >;
      content?: Record<string, { example?: unknown }>;
    }
  >;
  security?: Array<Record<string, unknown>>;
};

type GuideSection = {
  id: string;
  title: string;
  content: string;
  bullets?: string[];
  referenceLinks?: Array<{ label: string; path: string }>;
};

type DocsGuideResponse = {
  title?: string;
  source?: string;
  sections?: GuideSection[];
};

type OperationRow = {
  id: string;
  method: string;
  path: string;
  summary: string;
  details: OpenApiOperation;
};

type AssistantDocsKey = "tripist" | "explorist" | "aura";
type EmbedEnvironment = "production" | "staging";
type AssistantDocSection = {
  title: string;
  content: string;
  bullets?: string[];
};
type AssistantDocContent = {
  key: AssistantDocsKey;
  label: string;
  integrationName: string;
  sections: AssistantDocSection[];
};
type AssistantDocsNavItem = {
  key: AssistantDocsKey;
  label: string;
  integrationId: number;
  integrationExternalId: string | null;
};

const ASSISTANT_LABEL_BY_KEY: Record<AssistantDocsKey, string> = {
  tripist: "Tripist",
  explorist: "Explorist",
  aura: "Aura",
};

function buildAssistantDocSections(integrationName: string): AssistantDocSection[] {
  return [
    {
      title: "Quick Start",
      content:
        `${integrationName} supports both JavaScript embed and direct hosted URL launch. ` +
        "In both flows, integration settings are loaded from the configured backend integration.",
      bullets: [
        `Initialize ${integrationName} with tripist.init({ integrationId, elementSelector, environment }).`,
        "Use the hosted URL for direct/mobile entry with i/e query parameters.",
        "Use the preview route in Admin to validate integration behavior quickly.",
      ],
    },
    {
      title: "Identity Providers",
      content:
        `${integrationName} supports Firebase, HMAC, and Simulator identity providers using the same identity model.`,
      bullets: [
        "Firebase: authentication flow is handled by the widget.",
        "HMAC: delegated identity using external_user_id plus server-side hash.",
        "Simulator: test/development identity flow without verification.",
      ],
    },
  ];
}

const ASSISTANT_DOCS_CONTENT: Record<AssistantDocsKey, AssistantDocContent> = {
  tripist: {
    key: "tripist",
    label: `${ASSISTANT_LABEL_BY_KEY.tripist} Docs`,
    integrationName: ASSISTANT_LABEL_BY_KEY.tripist,
    sections: buildAssistantDocSections(ASSISTANT_LABEL_BY_KEY.tripist),
  },
  explorist: {
    key: "explorist",
    label: `${ASSISTANT_LABEL_BY_KEY.explorist} Docs`,
    integrationName: ASSISTANT_LABEL_BY_KEY.explorist,
    sections: buildAssistantDocSections(ASSISTANT_LABEL_BY_KEY.explorist),
  },
  aura: {
    key: "aura",
    label: `${ASSISTANT_LABEL_BY_KEY.aura} Docs`,
    integrationName: ASSISTANT_LABEL_BY_KEY.aura,
    sections: buildAssistantDocSections(ASSISTANT_LABEL_BY_KEY.aura),
  },
};

@Component({
  selector: "app-docs",
  templateUrl: "./docs.component.html",
  styleUrls: ["./docs.component.scss"],
})
export class DocsComponent implements OnInit, OnDestroy {
  loading = true;
  error = "";
  docsIndex: DocsIndexResponse | null = null;
  docsGuide: DocsGuideResponse | null = null;
  openApiSpec: OpenApiSpec | null = null;
  activeSection: "api-docs" | "api-reference" | "assistant-docs" = "api-docs";
  groupedOperations: Array<{
    tag: string;
    operations: OperationRow[];
  }> = [];
  selectedOperationId = "";
  availableAssistantDocs: AssistantDocsNavItem[] = [];
  selectedAssistantDocsKey: AssistantDocsKey | null = null;
  selectedAssistantIntegrationId: number | null = null;
  assistantEmbedExternalId = "";
  assistantEmbedEnvironment: EmbedEnvironment = "production";
  assistantEmbedCustomHtmlUrl = "";
  assistantEmbedCustomCssUrl = "";
  assistantEmbedHtmlCode = "";
  assistantEmbedScriptCode = "";
  assistantEmbedMobileUrl = "";
  assistantEmbedError = "";
  assistantEmbedGenerated = false;
  copiedCodeKind: "html" | "script" | "mobile" | null = null;
  private sessionSubscriptions: Subscription[] = [];

  constructor(
    private apiService: ApiService,
    private sessionService: SessionService,
    private integrationsService: IntegrationsService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
  ) {}

  ngOnInit() {
    this.loadDocs();
    this.loadAssistantDocs();

    // On hard refresh, session/account/app values may hydrate after component init.
    // Re-check integration-backed assistant docs whenever these change.
    this.sessionSubscriptions.push(
      this.sessionService.currentUser.subscribe(() => this.loadAssistantDocs()),
      this.sessionService.currentAccount.subscribe(() => this.loadAssistantDocs()),
      this.sessionService.currentApp.subscribe(() => this.loadAssistantDocs()),
      this.activatedRoute.data.subscribe(() => this.syncRouteState()),
      this.activatedRoute.paramMap.subscribe(() => this.syncRouteState()),
    );

    this.syncRouteState();
  }

  ngOnDestroy() {
    this.sessionSubscriptions.forEach((subscription) => subscription.unsubscribe());
    this.sessionSubscriptions = [];
  }

  get title() {
    return (
      this.openApiSpec?.info?.title ||
      this.docsIndex?.title ||
      "Lighthouse API Documentation"
    );
  }

  get version() {
    return this.openApiSpec?.info?.version || this.docsIndex?.version || "";
  }

  get description() {
    return "";
  }

  get downloadOpenApiJsonPath() {
    return this.normalizeApiPath(
      this.docsIndex?.docs?.openapiJson,
      "api/docs/openapi.json",
    );
  }

  get downloadOpenApiYamlPath() {
    return this.normalizeApiPath(
      this.docsIndex?.docs?.openapiYaml,
      "api/docs/openapi.yaml",
    );
  }

  get downloadRamlPath() {
    return this.normalizeApiPath(this.docsIndex?.docs?.raml, "api/docs/raml");
  }

  get guidePath() {
    return this.normalizeApiPath(this.docsIndex?.docs?.guide, "api/docs/guide");
  }

  get guideSections() {
    return this.docsGuide?.sections || [];
  }

  downloadOpenApiJson() {
    this.apiService.download(this.downloadOpenApiJsonPath, "openapi.json");
  }

  downloadOpenApiYaml() {
    this.apiService.download(this.downloadOpenApiYamlPath, "openapi.yaml");
  }

  downloadRaml() {
    this.apiService.download(this.downloadRamlPath, "api.raml");
  }

  showApiDocsSection() {
    this.router.navigate(["/docs/api"]);
  }

  showApiReferenceSection() {
    this.router.navigate(["/docs/api-reference"]);
  }

  showAssistantDocsSection(integrationId?: number) {
    if (!integrationId) {
      return;
    }
    this.router.navigate(["/docs/integration", integrationId]);
  }

  get isAuthenticated() {
    return !!this.sessionService.currentUserValue;
  }

  get selectedAssistantDocs() {
    if (!this.selectedAssistantDocsKey) {
      return null;
    }
    return ASSISTANT_DOCS_CONTENT[this.selectedAssistantDocsKey];
  }

  isAssistantDocsSelected(integrationId: number) {
    return this.activeSection === "assistant-docs" && this.selectedAssistantIntegrationId === integrationId;
  }

  generateAssistantEmbedCode() {
    const integrationExternalId = this.assistantEmbedExternalId.trim();
    const environment = this.assistantEmbedEnvironment;
    const customHtmlUrl = this.assistantEmbedCustomHtmlUrl.trim();
    const customCssUrl = this.assistantEmbedCustomCssUrl.trim();

    this.assistantEmbedError = "";
    this.assistantEmbedGenerated = false;
    this.copiedCodeKind = null;

    if (!integrationExternalId) {
      this.assistantEmbedError = "Unable to resolve integration external ID for this integration.";
      return;
    }

    const scriptUrl =
      environment === "staging"
        ? "https://tripist-staging.lighthousepe.ai/tripist.min.js"
        : "https://tripist.lighthousepe.ai/tripist.min.js";

    const configParts = [
      `integrationId: '${integrationExternalId}'`,
      `elementSelector: '#tripist-widget'`,
      `environment: '${environment}'`,
    ];
    if (customCssUrl) {
      configParts.push(`widgetCssUrl: '${customCssUrl}'`);
    }
    if (customHtmlUrl) {
      configParts.push(`widgetTemplateUrl: '${customHtmlUrl}'`);
    }
    const configString = configParts.join(",\n          ");
    const configStringScript = configParts.join(",\n        ");

    // Mirrors tripist/server.js /api/generate-embed-code output.
    this.assistantEmbedHtmlCode = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1, viewport-fit=cover">
  <title>Your Page with Tripist/Explorist</title>
  <style>
    html, body { height: 100%; margin: 0; }

    /* Mobile-first: full-screen overlay */
    #tripist-widget {
      position: fixed;
      inset: 0;
      width: 100vw;
      height: 100vh;   /* fallback */
      height: 100svh;  /* small viewport units */
      height: 100dvh;  /* dynamic viewport for iOS/Android */
      display: none;
      flex-direction: column;
      z-index: 9999;
      border-radius: 0;
    }

    /* Explicitly force full-screen on mobile to override inline styles from the widget */
    @media (max-width: 767px) {
      #tripist-widget {
        position: fixed !important;
        inset: 0 !important;
        width: 100vw !important;
        height: 100vh !important;
        height: 100svh !important;
        height: 100dvh !important;
        border-radius: 0 !important;
      }
    }

    /* ≥768px: docked panel in lower-right */
    @media (min-width: 768px) {
      #tripist-widget {
        position: fixed !important;
        bottom: 20px !important;
        right: 20px !important;
        left: auto !important;
        top: auto !important;
        width: 600px !important;
        height: 900px !important;
        border-radius: 12px !important;
        z-index: 9999 !important;
      }
    }
  </style>
  <style>
    body { font-family: Arial, sans-serif; padding: 20px; }
  </style>
</head>
<body>
  <h1>Your Website Content</h1>
  <p>Your existing content goes here...</p>

  <button onclick="initWidget()">Init Widget</button>
  <button id="close-widget-btn" onclick="closeWidget()" style="margin-left: 8px; display:none;">Close Widget</button>

  <div id="tripist-widget"></div>

  <script>
    document.addEventListener('DOMContentLoaded', function() {
      var script = document.createElement('script');
      script.src = '${scriptUrl}';
      script.onload = function() {
        console.log('Tripist script loaded successfully');
      };
      script.onerror = function() {
        console.error('Failed to load Tripist script');
      };
      document.head.appendChild(script);
    });

    function initWidget() {
      if (typeof tripist === 'undefined') {
        console.error('Tripist library not loaded');
        return;
      }

      try {
        var config = {
          ${configString}
        };

        console.log('Initializing Tripist with config:', config);
        tripist.init(config);
        var el = document.getElementById('tripist-widget');
        el.style.display = 'flex';
        var closeBtn = document.getElementById('close-widget-btn');
        if (closeBtn) closeBtn.style.display = 'inline-block';

        // expose the resize handler so it can be removed on close
        window._tripistApplyPlacement = function applyPlacement() {
          if (window.matchMedia('(min-width: 768px)').matches) {
            el.classList.add('widget-fixed');
          } else {
            el.classList.remove('widget-fixed');
          }
        };
        window._tripistApplyPlacement();
        window.addEventListener('resize', window._tripistApplyPlacement);
      } catch (error) {
        console.error('Error initializing Tripist:', error);
      }
    }

    function closeWidget() {
      try {
        var el = document.getElementById('tripist-widget');
        if (!el) return;
        if (window._tripistApplyPlacement) {
          window.removeEventListener('resize', window._tripistApplyPlacement);
        }
        if (typeof tripist !== 'undefined' && typeof tripist.reset === 'function') {
          tripist.reset();
        }
        el.innerHTML = '';
        el.style.display = 'none';
        var closeBtn = document.getElementById('close-widget-btn');
        if (closeBtn) closeBtn.style.display = 'none';
      } catch (e) {
        console.error('Error closing Tripist widget:', e);
      }
    }
  </script>
</body>
</html>`;

    this.assistantEmbedScriptCode = `<!-- Add this HTML element where you want the widget to appear -->
<div id="tripist-widget"></div>

<!-- Recommended CSS for responsive widget placement -->
<style>
  #tripist-widget {
    position: fixed;
    inset: 0;
    width: 100vw;
    height: 100vh;
    height: 100svh;
    height: 100dvh;
    display: none;
    flex-direction: column;
    z-index: 9999;
    border-radius: 0;
  }

  @media (max-width: 767px) {
    #tripist-widget {
      position: fixed !important;
      inset: 0 !important;
      width: 100vw !important;
      height: 100vh !important;
      height: 100svh !important;
      height: 100dvh !important;
      border-radius: 0 !important;
    }
  }

  @media (min-width: 768px) {
    #tripist-widget {
      position: fixed !important;
      bottom: 20px !important;
      right: 20px !important;
      left: auto !important;
      top: auto !important;
      width: 600px !important;
      height: 900px !important;
      border-radius: 12px !important;
      z-index: 9999 !important;
    }
  }
</style>

<!-- Add this script to load Tripist. Call initWidget() when ready to initialize.
     Use closeWidget() to hide and reset the widget. -->
<script>
(function() {
  function loadTripist(callback) {
    var script = document.createElement('script');
    script.src = '${scriptUrl}';
    script.onload = function() {
      console.log('Tripist script loaded successfully');
      if (callback) callback();
    };
    script.onerror = function() {
      console.error('Failed to load Tripist script');
    };
    document.head.appendChild(script);
  }

  window.initWidget = function() {
    if (typeof tripist === 'undefined') {
      console.error('Tripist library not loaded');
      return;
    }
    try {
      var config = {
        ${configStringScript}
      };
      console.log('Initializing Tripist with config:', config);
      tripist.init(config);
      var el = document.getElementById('tripist-widget');
      el.style.display = 'flex';
      window._tripistApplyPlacement = function applyPlacement() {
        if (window.matchMedia('(min-width: 768px)').matches) {
          el.classList.add('widget-fixed');
        } else {
          el.classList.remove('widget-fixed');
        }
      };
      window._tripistApplyPlacement();
      window.addEventListener('resize', window._tripistApplyPlacement);
    } catch (error) {
      console.error('Error initializing Tripist:', error);
    }
  };

  window.closeWidget = function() {
    try {
      var el = document.getElementById('tripist-widget');
      if (!el) return;
      if (window._tripistApplyPlacement) {
        window.removeEventListener('resize', window._tripistApplyPlacement);
      }
      if (typeof tripist !== 'undefined' && typeof tripist.reset === 'function') {
        tripist.reset();
      }
      el.innerHTML = '';
      el.style.display = 'none';
    } catch (e) {
      console.error('Error closing Tripist widget:', e);
    }
  };

  if (typeof tripist === 'undefined') {
    loadTripist(function() { /* ready for initWidget() */ });
  }
})();
</script>`;

    const baseUrl = scriptUrl.replace("/tripist.min.js", "");
    let mobileUrl = `${baseUrl}/?i=${integrationExternalId}&e=${environment}`;
    if (customHtmlUrl) {
      mobileUrl += `&wt=${encodeURIComponent(customHtmlUrl)}`;
    }
    if (customCssUrl) {
      mobileUrl += `&wc=${encodeURIComponent(customCssUrl)}`;
    }
    this.assistantEmbedMobileUrl = mobileUrl;
    this.assistantEmbedGenerated = true;
  }

  copyAssistantEmbedCode(kind: "html" | "script" | "mobile") {
    const text =
      kind === "html"
        ? this.assistantEmbedHtmlCode
        : kind === "script"
          ? this.assistantEmbedScriptCode
          : this.assistantEmbedMobileUrl;

    if (!text) {
      return;
    }
    navigator.clipboard.writeText(text).then(() => {
      this.copiedCodeKind = kind;
      setTimeout(() => {
        if (this.copiedCodeKind === kind) {
          this.copiedCodeKind = null;
        }
      }, 1500);
    });
  }

  selectOperation(operation: OperationRow) {
    this.selectedOperationId = operation.id;
  }

  isSelected(operation: OperationRow) {
    return this.selectedOperationId === operation.id;
  }

  get selectedOperation() {
    for (const group of this.groupedOperations) {
      const operation = group.operations.find((item) => item.id === this.selectedOperationId);
      if (operation) return operation;
    }
    return null;
  }

  parameterSummary(operation: OperationRow) {
    const params = operation.details.parameters || [];
    if (params.length === 0) return "None";
    return params
      .map((p) => {
        const required = p.required ? "required" : "optional";
        const location = p.in || "unknown";
        return `${p.name || "param"} (${location}, ${required})`;
      })
      .join(", ");
  }

  requestBodySummary(operation: OperationRow) {
    const content = operation.details.requestBody?.content || {};
    const contentTypes = Object.keys(content);
    if (contentTypes.length === 0) return "None";
    return contentTypes.join(", ");
  }

  responseCodeSummary(operation: OperationRow) {
    const responses = operation.details.responses || {};
    const codes = Object.keys(responses);
    if (codes.length === 0) return "None";
    return codes.sort().join(", ");
  }

  responseHeaderSummary(operation: OperationRow) {
    const responses = operation.details.responses || {};
    const response200 = responses["200"];
    if (!response200 || !response200.headers) return "None";
    const headers = response200.headers;
    const headerKeys = Object.keys(headers);
    if (headerKeys.length === 0) return "None";
    return headerKeys
      .map((headerName) => {
        const header = headers[headerName] || {};
        const description = header.description ? ` - ${header.description}` : "";
        const example = header.example ?? header.schema?.example;
        let exampleText = "";
        if (example !== undefined) {
          exampleText =
            typeof example === "string"
              ? ` (example: ${example})`
              : ` (example: ${JSON.stringify(example)})`;
        }
        return `${headerName}${description}${exampleText}`;
      })
      .join("; ");
  }

  responseExample(operation: OperationRow) {
    const responses = operation.details.responses || {};
    const prioritized = ["200", ...Object.keys(responses).sort()];
    for (const code of prioritized) {
      const response = responses[code];
      if (!response || !response.content) continue;
      const firstContentType = Object.keys(response.content)[0];
      if (!firstContentType) continue;
      const example = response.content[firstContentType]?.example;
      if (example === undefined) continue;
      if (typeof example === "string" && firstContentType === "text/html") {
        return example;
      }
      try {
        return JSON.stringify(example, null, 2);
      } catch (_e) {
        return "";
      }
    }
    return "";
  }

  hasResponseExample(operation: OperationRow) {
    return this.responseExample(operation).length > 0;
  }

  requestExample(operation: OperationRow) {
    const content = operation.details.requestBody?.content || {};
    const firstContentType = Object.keys(content)[0];
    if (!firstContentType) return "";
    const example = content[firstContentType]?.example;
    if (example === undefined) return "";
    try {
      return JSON.stringify(example, null, 2);
    } catch (_e) {
      return "";
    }
  }

  hasRequestExample(operation: OperationRow) {
    return this.requestExamples(operation).length > 0;
  }

  requestExamples(operation: OperationRow) {
    const content = operation.details.requestBody?.content || {};
    const firstContentType = Object.keys(content)[0];
    if (!firstContentType) return [];
    const entry = content[firstContentType];
    const namedExamples = entry?.examples || {};
    const namedKeys = Object.keys(namedExamples);
    if (namedKeys.length > 0) {
      return namedKeys.map((key) => {
        const ex = namedExamples[key] || {};
        let json = "";
        try {
          json = JSON.stringify(ex.value, null, 2);
        } catch (_e) {
          json = "";
        }
        return {
          label: ex.summary || key,
          json,
        };
      });
    }
    const fallback = this.requestExample(operation);
    if (!fallback) return [];
    return [{ label: "Example", json: fallback }];
  }

  securitySummary(operation: OperationRow) {
    const security = operation.details.security || [];
    if (security.length === 0) return "None";
    const schemes = this.openApiSpec?.components?.securitySchemes || {};
    return security
      .map((entry) => {
        const keys = Object.keys(entry);
        if (keys.length === 0) return "";
        return keys
          .map((key) => {
            const scheme = schemes[key];
            if (!scheme) return key;
            const headerName = scheme.name || key;
            const details = scheme.description || "";
            return `${headerName} header${details ? ` - ${details}` : ""}`;
          })
          .join(", ");
      })
      .filter((s) => s.trim().length > 0)
      .join(" | ");
  }

  private loadDocs() {
    this.loading = true;
    this.error = "";

    this.apiService.get<DocsIndexResponse>("api/docs").subscribe(
      (index) => {
        this.docsIndex = index || {};
        this.apiService.get<DocsGuideResponse>(this.guidePath).subscribe(
          (guide) => {
            this.docsGuide = guide || {};
            this.apiService.get<OpenApiSpec>("api/docs/openapi.json").subscribe(
              (spec) => {
                this.openApiSpec = spec || {};
                this.groupedOperations = this.buildGroupedOperations(spec || {});
                this.ensureSelectedOperation();
                this.loading = false;
              },
              () => {
                this.error = "Unable to load OpenAPI specification.";
                this.loading = false;
              },
            );
          },
          () => {
            this.error = "Unable to load API guide content.";
            this.loading = false;
          },
        );
      },
      () => {
        this.error = "Unable to load API documentation index.";
        this.loading = false;
      },
    );
  }

  private buildGroupedOperations(spec: OpenApiSpec) {
    const groups: Record<string, OperationRow[]> = {};
    const paths = spec.paths || {};

    Object.keys(paths).forEach((apiPath) => {
      const methods = paths[apiPath] || {};
      Object.keys(methods).forEach((methodName) => {
        const op = methods[methodName] || {};
        const tag = (op.tags && op.tags[0]) || "General";
        groups[tag] = groups[tag] || [];
        groups[tag].push({
          id: `${methodName.toUpperCase()} ${apiPath}`,
          method: methodName.toUpperCase(),
          path: apiPath,
          summary: op.summary || "",
          details: op,
        });
      });
    });

    return Object.keys(groups)
      .sort((a, b) => a.localeCompare(b))
      .map((tag) => ({
        tag,
        operations: groups[tag].sort((a, b) => {
          if (a.path === b.path) return a.method.localeCompare(b.method);
          return a.path.localeCompare(b.path);
        }),
      }));
  }

  private normalizeApiPath(pathValue: string | undefined, fallback: string) {
    return (pathValue || fallback).replace(/^\/+/, "");
  }

  private ensureSelectedOperation() {
    if (this.selectedOperation) return;
    const firstGroup = this.groupedOperations[0];
    const firstOperation = firstGroup && firstGroup.operations[0];
    this.selectedOperationId = firstOperation ? firstOperation.id : "";
  }

  private loadAssistantDocs() {
    if (!this.isAuthenticated) {
      this.availableAssistantDocs = [];
      this.selectedAssistantDocsKey = null;
      this.selectedAssistantIntegrationId = null;
      return;
    }

    const accountId = this.sessionService.currentAccountValue?.accountID;
    const appId = this.sessionService.currentAppValue?.id;
    if (!accountId || !appId) {
      this.availableAssistantDocs = [];
      this.selectedAssistantDocsKey = null;
      this.selectedAssistantIntegrationId = null;
      return;
    }

    this.integrationsService.getIntegrations(accountId, appId, false).subscribe(
      (response: any) => {
        const records = Array.isArray(response) ? response : response?.records || [];
        const seenByKey = new Set<AssistantDocsKey>();
        const navItems: AssistantDocsNavItem[] = [];
        records.forEach((integration: any) => {
          const providerType = integration?.provider?.type;
          const integrationId = integration?.id;
          const integrationExternalId = integration?.external_id || null;
          const key = this.getAssistantDocsKeyForProviderType(providerType);
          if (!key || !integrationId || seenByKey.has(key)) {
            return;
          }
          seenByKey.add(key);
          navItems.push({
            key,
            label: ASSISTANT_DOCS_CONTENT[key].label,
            integrationId: Number(integrationId),
            integrationExternalId,
          });
        });
        this.availableAssistantDocs = navItems;

        if (!this.availableAssistantDocs.length) {
          this.selectedAssistantDocsKey = null;
          this.selectedAssistantIntegrationId = null;
          if (this.activeSection === "assistant-docs") {
            this.router.navigate(["/docs/api"]);
          }
          return;
        }
        this.resolveAssistantSelectionFromRoute();
      },
      () => {
        this.availableAssistantDocs = [];
        this.selectedAssistantDocsKey = null;
        this.selectedAssistantIntegrationId = null;
      },
    );
  }

  private syncRouteState() {
    const section = this.activatedRoute.snapshot.data["docsSection"] as
      | "api-docs"
      | "api-reference"
      | "assistant-docs"
      | undefined;
    this.activeSection = section || "api-docs";
    if (this.activeSection === "api-reference") {
      this.ensureSelectedOperation();
    }
    this.resolveAssistantSelectionFromRoute();
  }

  private resolveAssistantSelectionFromRoute() {
    if (this.activeSection !== "assistant-docs") {
      return;
    }
    const integrationIdRaw = this.activatedRoute.snapshot.paramMap.get("integrationId");
    const integrationId = integrationIdRaw ? Number(integrationIdRaw) : NaN;
    if (!Number.isFinite(integrationId)) {
      this.selectedAssistantDocsKey = null;
      this.selectedAssistantIntegrationId = null;
      return;
    }

    const matched = this.availableAssistantDocs.find((item) => item.integrationId === integrationId);
    if (matched) {
      this.selectedAssistantIntegrationId = matched.integrationId;
      this.selectedAssistantDocsKey = matched.key;
      this.assistantEmbedExternalId = matched.integrationExternalId || "";
      this.assistantEmbedGenerated = false;
      this.assistantEmbedError = "";
      return;
    }

    this.selectedAssistantIntegrationId = integrationId;
    this.resolveAssistantDocsByIntegrationId(integrationId);
  }

  private resolveAssistantDocsByIntegrationId(integrationId: number) {
    const accountId = this.sessionService.currentAccountValue?.accountID;
    const appId = this.sessionService.currentAppValue?.id;
    if (!accountId || !appId) {
      return;
    }

    this.integrationsService.getIntegration(accountId, integrationId, appId).subscribe(
      (integration: any) => {
        const key = this.getAssistantDocsKeyForProviderType(integration?.provider?.type);
        if (!key) {
          this.selectedAssistantDocsKey = null;
          return;
        }
        this.selectedAssistantDocsKey = key;
        const exists = this.availableAssistantDocs.some((item) => item.integrationId === integrationId);
        if (!exists) {
          this.availableAssistantDocs = [
            ...this.availableAssistantDocs,
            {
              key,
              label: ASSISTANT_DOCS_CONTENT[key].label,
              integrationId,
              integrationExternalId: integration?.external_id || null,
            },
          ];
        }
        this.assistantEmbedExternalId = integration?.external_id || "";
        this.assistantEmbedGenerated = false;
        this.assistantEmbedError = "";
      },
      () => {
        this.selectedAssistantDocsKey = null;
      },
    );
  }

  private getAssistantDocsKeyForProviderType(providerType: string | undefined): AssistantDocsKey | null {
    if (providerType === "tripist_assistant") {
      return "tripist";
    }
    if (providerType === "explorist_assistant") {
      return "explorist";
    }
    if (providerType === "aura_assistant") {
      return "aura";
    }
    return null;
  }
}
